import React from "react";
import { Box, Button, IconButton, InputBase, MenuItem, Select, Typography } from "@mui/material";
import MaterialTable from "material-table";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import FilterListIcon from "@mui/icons-material/FilterList";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { EmptyState, LoadingSkeleton } from "./adminPrimitives";
import adminTokens from "../styles/adminDesignTokens";

export const AdminFilterBar = ({
  searchValue,
  onSearchChange,
  onSearchClear,
  searchPlaceholder = "Search records",
  filters,
  onReset,
  actions
}) => (
  <Box
    className="garahex-admin-filterBar"
    sx={{
      display: "flex",
      alignItems: "center",
      flexWrap: "wrap",
      gap: 1.2,
      mb: 1.5
    }}
  >
    {onSearchChange ? (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.8,
          minWidth: { xs: "100%", sm: 280 },
          minHeight: adminTokens.density.controlHeight,
          px: 1.2,
          borderRadius: 999,
          bgcolor: "#fff",
          border: `1px solid ${adminTokens.colors.border}`,
          boxShadow: "0 10px 24px rgba(3, 29, 54, 0.06)",
          "&:focus-within": { boxShadow: adminTokens.shadows.focus, borderColor: "rgba(11,124,255,.5)" }
        }}
        role="search"
      >
        <SearchIcon fontSize="small" sx={{ color: adminTokens.colors.muted }} />
        <InputBase
          fullWidth
          value={searchValue || ""}
          onChange={(event) => onSearchChange(event.target.value)}
          placeholder={searchPlaceholder}
          inputProps={{ "aria-label": searchPlaceholder }}
        />
        {searchValue && onSearchClear ? (
          <IconButton size="small" aria-label="Clear search" onClick={onSearchClear}>
            <ClearIcon fontSize="small" />
          </IconButton>
        ) : null}
      </Box>
    ) : null}
    {filters}
    {onReset ? (
      <Button variant="text" onClick={onReset} sx={{ borderRadius: 999, textTransform: "none", fontWeight: 850 }}>
        Reset Filters
      </Button>
    ) : null}
    {actions ? <Box sx={{ ml: "auto", display: "flex", gap: 1, flexWrap: "wrap" }}>{actions}</Box> : null}
  </Box>
);

export const AdminSelectFilter = ({ label, value, onChange, options = [] }) => (
  <Select
    size="small"
    displayEmpty
    value={value}
    onChange={onChange}
    renderValue={(selected) => selected || label}
    IconComponent={FilterListIcon}
    sx={{
      minWidth: 168,
      minHeight: adminTokens.density.controlHeight,
      borderRadius: 999,
      bgcolor: "#fff",
      fontWeight: 800,
      fontSize: "0.78rem",
      "& .MuiOutlinedInput-notchedOutline": { borderColor: adminTokens.colors.border }
    }}
    inputProps={{ "aria-label": label }}
  >
    {options.map((option) => (
      <MenuItem key={option.value ?? option} value={option.value ?? option}>
        {option.label ?? option}
      </MenuItem>
    ))}
  </Select>
);

export const AdminRowActionButton = ({ label = "Actions", children, ...props }) => (
  <IconButton
    aria-label={label}
    size="small"
    sx={{
      width: 32,
      height: 32,
      borderRadius: 2,
      color: adminTokens.colors.deepNavy,
      bgcolor: "rgba(11,124,255,.08)",
      "&:hover": { bgcolor: "rgba(255,107,0,.13)" }
    }}
    {...props}
  >
    {children || <MoreVertIcon fontSize="small" />}
  </IconButton>
);

const tableOptions = {
  search: true,
  paging: true,
  sorting: true,
  actionsColumnIndex: -1,
  headerStyle: {
    position: "sticky",
    top: 0,
    zIndex: 1,
    backgroundColor: "#F7FBFF",
    color: adminTokens.colors.deepNavy,
    fontWeight: 850,
    fontSize: "0.72rem",
    padding: adminTokens.density.tableCellPadding,
    borderBottom: `1px solid ${adminTokens.colors.border}`
  },
  rowStyle: {
    color: adminTokens.colors.text,
    fontSize: "0.78rem",
    minHeight: adminTokens.density.tableRowHeight
  },
  searchFieldStyle: {
    borderRadius: 999,
    paddingLeft: 10,
    fontSize: "0.78rem"
  },
  pageSize: 15,
  pageSizeOptions: [5, 10, 20, 50],
  draggable: false
};

const AdminDataTable = ({
  title,
  columns,
  data,
  style,
  actions,
  options,
  localization,
  isLoading,
  error,
  emptyTitle,
  emptyDescription,
  ...props
}) => {
  if (error) {
    return (
      <Box className="garahex-admin-tableShell" role="alert">
        <Typography sx={{ fontWeight: 900, color: adminTokens.colors.danger }}>
          {typeof error === "string" ? error : "Unable to load records."}
        </Typography>
      </Box>
    );
  }

  if (isLoading) {
    return (
      <Box className="garahex-admin-tableShell">
        <LoadingSkeleton height={54} sx={{ mb: 1.2 }} />
        <LoadingSkeleton height={54} sx={{ mb: 1.2 }} />
        <LoadingSkeleton height={54} />
      </Box>
    );
  }

  if (Array.isArray(data) && data.length === 0 && emptyTitle) {
    return (
      <Box className="garahex-admin-tableShell">
        <EmptyState title={emptyTitle} description={emptyDescription} />
      </Box>
    );
  }

  return (
    <Box className="garahex-admin-tableShell">
      <MaterialTable
        title={title}
        columns={columns}
        data={data}
        style={{
          boxShadow: "none",
          borderRadius: adminTokens.radius.lg,
          overflow: "hidden",
          background: "transparent",
          ...style
        }}
        actions={actions}
        options={{ ...tableOptions, ...options }}
        localization={localization}
        {...props}
      />
    </Box>
  );
};

export default AdminDataTable;
